import React, {createRef, ReactNode} from "react";
import {ViewerMode} from "../ViewerMode";
import MouseMode from "../settings/MouseMode";
import MessageBox from "../../../shared/components/MessageBox";
import i18next from "i18next";
import {MessageType} from "../../../shared/log/MessageInterface";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {TextViewerDefinition, TextViewerProperties, TextViewerState} from "../text/TextViewer";

export interface HtmlViewerProperties extends TextViewerProperties {
	viewAsHTML?: boolean
}

export interface HtmlViewerState extends TextViewerState {
}

export class HtmlViewerDefinition extends TextViewerDefinition<HtmlViewerProperties, HtmlViewerState> {
	public static defaults(): HtmlViewerProperties {
		return {
			viewerMode: ViewerMode.DEFAULT,
			mouseMode: MouseMode.NONE
		} as HtmlViewerProperties;
	}

	constructor(props: Readonly<HtmlViewerProperties>) {
		super(props);

		this.state = {
			...this.state
		};

		this.loaderElement = createRef<HTMLDivElement>();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<HtmlViewerProperties>, prevState: Readonly<HtmlViewerState>
	): Promise<void> {
		if (this.state.loading) {
			return;
		}

		if (this.props.viewAsHTML !== prevProps.viewAsHTML) {
			this.computeViewport();
		}
	}

	protected computeViewport(): void {
		if (this.props.viewAsHTML) {
			return;
		}

		super.computeViewport();
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (this.state.loading) {
			return (
				<div className="viewer html" ref={this.viewerElement}>
					<div className="loader" ref={this.loaderElement}>
						<span className="icon icon-spinner spin"/>
					</div>
				</div>
			);
		}

		if (FunctionHelper.exists(this.state.error)) {
			return (
				<div className="viewer html" ref={this.viewerElement}>
					{(this.props.viewerMode === ViewerMode.PREVIEW || this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW) &&
						<div className="fallback-icon">
							<span className="icon icon-code"/>
						</div>
					}
					{this.props.viewerMode !== ViewerMode.PREVIEW && this.props.viewerMode !== ViewerMode.PORTFOLIO_PREVIEW &&
						<MessageBox type={MessageType.DANGER} language={i18next.language}>
							{this.state.error}
						</MessageBox>
					}
				</div>
			);
		}

		if (this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			return (
				<div className="fallback-icon">
					<span className="icon icon-code"/>
				</div>
			);
		}

		let viewerDataValue: string = FunctionHelper.applyValue(this.state.viewerData!.value, "");
		let viewerClasses: string = "text-viewer";
		if (this.props.wrapText) {
			viewerClasses += " wrap";
		}

		if (this.props.viewAsHTML) {
			viewerDataValue = FunctionHelper.applyValue(this.state.viewerData!.rawValue, "");
		}

		return (
			<>
				{this.props.viewAsHTML ?
					<div className="viewer html" ref={this.viewerElement}>
						<div className="html-viewer move" onPointerDown={
							(event) => this.handleMouseDown(event.nativeEvent)
						}>
							<iframe sandbox="" srcDoc={viewerDataValue} title="viewer"/>
						</div>
					</div> :
					<div className="viewer text" ref={this.viewerElement} onScroll={() => this.computeViewport()}>
						<div className={viewerClasses}>
							{this.renderLines()}
						</div>
					</div>
				}
			</>
		);
	}
}

const HtmlViewer = React.memo(HtmlViewerDefinition,
	(prevProps: Readonly<HtmlViewerProperties>, nextProps: Readonly<HtmlViewerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument) &&
			(
				nextProps.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
				nextProps.portalDocument.get().updated !== prevProps.portalDocument?.get().updated
			)
		) {
			return false;
		}

		if (nextProps.attachmentKey !== prevProps.attachmentKey) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		if (nextProps.wrapText !== prevProps.wrapText) {
			return false;
		}

		if (nextProps.viewAsHTML !== prevProps.viewAsHTML) {
			return false;
		}

		return true;
	}
);

export default HtmlViewer;